import './MapContextMenu.scss'

import { HDSButton } from '@here/hds-react-components'
import classNames from 'classnames'
import { RouteWaypoint } from 'data/model/routePlanner'
import { SearchStateListItem } from 'data/model/search'
import { RootState } from 'data/reducer'
import { setContextMenu } from 'data/reducer/map'
import { setPdcItem } from 'data/reducer/pdc'
import { setSelectedRoute } from 'data/reducer/routePlanner'
import { useAppDispatch, useAppSelector } from 'data/store/hooks'
import { createDirectionsPath, Waypoint, WaypointType } from 'lib/directions'
import { PageType, useNavigation } from 'lib/hooks'
import { i18n } from 'lib/i18n'
import { Position, serializeCompactV0 } from 'lib/place'
import { createMap } from 'map/map'
import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { isSourceDestinationSet, sliceEmptyWaypoints } from 'utils/routeplanner'

export const MapContextMenuWrapper = (): JSX.Element => {
  const dispatch = useAppDispatch()
  const wrapperRef = useRef(null)

  const clearMapContextMenu = (): void => {
    dispatch(setContextMenu({}))
  }

  const useOutsideAlerter = (ref: RefObject<HTMLDivElement>): void => {
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent): void => {
        if (event.target && ref.current && !ref.current.contains(event.target as Node)) {
          clearMapContextMenu()
        }
      }

      window.addEventListener('mousedown', handleClickOutside)

      // I am removing the context menu on screen changes, as the user may lose the focus
      window.addEventListener('wheel', clearMapContextMenu)
      window.addEventListener('resize', clearMapContextMenu)

      return () => {
        window.removeEventListener('mousedown', handleClickOutside)
        window.removeEventListener('wheel', clearMapContextMenu)
        window.removeEventListener('resize', clearMapContextMenu)
      }
    }, [ref])
  }

  useOutsideAlerter(wrapperRef)

  return (
    <div ref={wrapperRef}>
      <MapContextMenu onClick={clearMapContextMenu} />
    </div>
  )
}

interface MapContextMenuProps {
  onClick: () => void
}

const MapContextMenu = ({ onClick }: MapContextMenuProps): JSX.Element => {
  const map = createMap()
  const dispatch = useAppDispatch()
  const { goTo } = useNavigation()

  const [rightScreenLimit, setRightScreenLimit] = useState<number>(window.innerWidth - 336)
  const [bottomScreenLimit, setBottomScreenLimit] = useState<number>(window.innerHeight - 336)
  const setStartingPointLabel = i18n('map-context-menu.set-as-starting-point')
  const setDestinationLabel = i18n('map-context-menu.set-as-destination')
  const addStopLabel = i18n(`route-planner.add-stop-input-placeholder`)

  const routeWaypoints = useAppSelector((state: RootState) => state.routePlanner.routeWaypoints)
  const contextMenuState = useAppSelector((state) => state.map.contextMenu)
  const locationAddress = useAppSelector((state) => state.search.locationAddress)
  const isLoading = useAppSelector((state) => state.search.isLoading)
  const isRoutePlannerOpen = location.pathname.startsWith('/directions')
  const hasValidRoute = isSourceDestinationSet(routeWaypoints)

  const handleScreenResize = useCallback((): void => {
    setRightScreenLimit(window.innerWidth - 336)
    setBottomScreenLimit(window.innerHeight - 336)
  }, [])

  useEffect(() => {
    window.addEventListener('resize', handleScreenResize)
    return () => window.removeEventListener('resize', handleScreenResize)
  })

  if (!contextMenuState.position) return <></>

  const viewPoint = map.geoToScreen({
    lat: contextMenuState.position.lat,
    lng: contextMenuState.position.lng,
  })

  if (!viewPoint) return <></>

  if (isLoading) return <></>

  const handleOnAddressClick = (item: SearchStateListItem): void => {
    onClick()
    const serializedPdcId = serializeCompactV0({
      access: contextMenuState.position as Position,
      address: (item.address && item.address.label) || '',
      categoryId: item.categoryId !== undefined ? item.categoryId : '',
      countryCode: '',
      id: item.id,
      placeType: '',
      politicalView: '',
      position: contextMenuState.position as Position,
      title: getLabel(item),
    })
    if (isRoutePlannerOpen) {
      dispatch(setSelectedRoute(undefined))
      dispatch(
        setPdcItem({
          id: item.id,
          position: contextMenuState.position,
          title: getLabel(item),
        })
      )
    } else {
      goTo({
        type: PageType.PDC,
        url: `/p/${encodeURIComponent(serializedPdcId)}`,
      })
    }
  }

  const handleAddToRouteClick = (
    item: SearchStateListItem,
    location: 'start' | 'destination' | 'stop'
  ): void => {
    if (!contextMenuState.position) {
      return
    }
    onClick()
    const place = getLabel(item)
    const lat = contextMenuState.position.lat
    const lng = contextMenuState.position.lng

    const newWaypoints = [...routeWaypoints]

    if (location === 'start') {
      newWaypoints[0] = { lat, lng, place } as RouteWaypoint
    } else if (location === 'destination') {
      const destIndex = newWaypoints.length < 2 ? 1 : newWaypoints.length - 1
      newWaypoints[destIndex] = { lat, lng, place } as RouteWaypoint
    } else {
      newWaypoints.push({ lat, lng, place })
    }

    const waypoints: Waypoint[] = sliceEmptyWaypoints(newWaypoints).map((waypoint) => {
      return {
        lat: waypoint.lat,
        lng: waypoint.lng,
        name: encodeURIComponent(waypoint.place),
        type: WaypointType.full,
      }
    })

    if (waypoints.length === 1 && location === 'destination') {
      waypoints.unshift({
        type: WaypointType.myLocation,
      })
    }

    const path = createDirectionsPath(waypoints)
    goTo({
      type: PageType.ROUTE_PLANNER,
      url: `/directions/${path}`,
    })
    dispatch(setSelectedRoute(undefined))
  }

  const isCloseToRightEdge = viewPoint.x >= rightScreenLimit
  const isCloseToBottomEdge = viewPoint.y >= bottomScreenLimit

  const getLabel = ({ address }: SearchStateListItem): string => {
    if (address !== undefined && address.street) {
      if (address.houseNumber) {
        return `${address.street}, ${address.houseNumber}`
      }

      return `${address.street}`
    }

    return address !== undefined ? address.label : ''
  }
  const bottom = isCloseToBottomEdge ? `calc(100vh - ${viewPoint.y + 40}px` : 'auto'
  const left = viewPoint.x - 40
  const top = isCloseToBottomEdge ? 'auto' : viewPoint.y - 40

  return (
    <div
      id="MapContextMenu"
      className={classNames('MapContextMenu', {
        'MapContextMenu--left': isCloseToRightEdge,
        'MapContextMenu--up': isCloseToBottomEdge,
      })}
      style={{ bottom, left, top } as React.CSSProperties}
    >
      {locationAddress !== undefined &&
        locationAddress[0] !== undefined &&
        locationAddress[0].address !== undefined && (
          <div className="MapContextMenu__container">
            <div className="MapContextMenu__row">
              <span className="MapContextMenu__Circle">
                <span className="MapContextMenu__Pointer" />
              </span>
              <div
                className={classNames('MapContextMenu__Pill', {
                  'MapContextMenu__Pill--left': isCloseToRightEdge,
                })}
                onClick={() => handleOnAddressClick(locationAddress[0])}
              >
                {getLabel(locationAddress[0])}
              </div>
            </div>
            <div className="MapContextMenu__row">
              <span className="MapContextMenu__icon_container">
                <HDSButton
                  icon="current-location"
                  iconCategory="map-view"
                  variant="floating"
                  size="medium"
                  onClick={() => handleAddToRouteClick(locationAddress[0], 'start')}
                />
              </span>
              <div
                className={classNames('MapContextMenu__Pill', {
                  'MapContextMenu__Pill--left': isCloseToRightEdge,
                })}
                onClick={() => handleAddToRouteClick(locationAddress[0], 'start')}
              >
                {setStartingPointLabel}
              </div>
            </div>
            {hasValidRoute && isRoutePlannerOpen && (
              <div className="MapContextMenu__row">
                <span className="MapContextMenu__icon_container">
                  <HDSButton
                    icon="plus"
                    iconCategory="core-ui"
                    variant="floating"
                    size="medium"
                    onClick={() => handleAddToRouteClick(locationAddress[0], 'stop')}
                  />
                </span>
                <div
                  className={classNames('MapContextMenu__Pill', {
                    'MapContextMenu__Pill--left': isCloseToRightEdge,
                  })}
                  onClick={() => handleAddToRouteClick(locationAddress[0], 'stop')}
                >
                  {addStopLabel}
                </div>
              </div>
            )}
            <div className="MapContextMenu__row">
              <span className="MapContextMenu__icon_container">
                <HDSButton
                  icon="destination"
                  iconCategory="travel-transport-tracking"
                  variant="floating"
                  size="medium"
                  onClick={() => handleAddToRouteClick(locationAddress[0], 'destination')}
                />
              </span>
              <div
                className={classNames('MapContextMenu__Pill', {
                  'MapContextMenu__Pill--left': isCloseToRightEdge,
                })}
                onClick={() => handleAddToRouteClick(locationAddress[0], 'destination')}
              >
                {setDestinationLabel}
              </div>
            </div>
          </div>
        )}
    </div>
  )
}
